'use client'

import type { BaseKey } from '@refinedev/core'
import type { PropsWithChildren, ReactNode } from 'react'

import {
  useBack,
  useResourceParams,
  useUserFriendlyName,
} from '@refinedev/core'
import { ArrowLeftIcon } from 'lucide-react'
import { RefreshButton } from '~/components/refineUi/buttons/refresh'
import { Breadcrumb } from '~/components/refineUi/layout/breadcrumb'
import { Button } from '~/components/ui/button'
import { Separator } from '~/components/ui/separator'
import { cn } from '~/lib/utils'
import { EditButton } from '../buttons/edit'

type ShowViewProps = PropsWithChildren<{
  className?: string
}>

export function ShowView({ children, className }: ShowViewProps) {
  return (
    <div className={cn('flex flex-col', 'gap-4', className)}>{children}</div>
  )
}

type ShowViewHeaderProps = PropsWithChildren<{
  resource?: string
  title?: string
  /** next to the title, e.g. a status badge */
  titleBadge?: ReactNode
  /**
   * the record Refresh reloads; default: `:id` from the URL, which on a
   * nested page (a game's config version) is the parent's
   */
  recordItemId?: BaseKey
  wrapperClassName?: string
  headerClassName?: string
  actionsSlot?: React.ReactNode
  /** default: whether the resource has an edit route */
  canEdit?: boolean
}>

export const ShowViewHeader = ({
  resource: resourceFromProps,
  title: titleFromProps,
  titleBadge,
  recordItemId: recordItemIdFromProps,
  actionsSlot,
  canEdit,
  wrapperClassName,
  headerClassName,
}: ShowViewHeaderProps) => {
  const back = useBack()

  const getUserFriendlyName = useUserFriendlyName()

  const { resource, identifier } = useResourceParams({
    resource: resourceFromProps,
  })
  const { id: recordItemIdFromUrl } = useResourceParams()
  const recordItemId = recordItemIdFromProps ?? recordItemIdFromUrl

  const resourceName = resource?.name ?? identifier
  const isEditButtonVisible = canEdit ?? Boolean(resource?.edit)

  const title =
    titleFromProps ??
    getUserFriendlyName(
      resource?.meta?.label ?? identifier ?? resource?.name,
      'singular',
    )

  return (
    <div className={cn('flex flex-col', 'gap-4', wrapperClassName)}>
      <div className="flex items-center relative gap-2">
        <div className="bg-background z-[2] pr-4">
          <Breadcrumb />
        </div>
        <Separator className={cn('absolute', 'left-0', 'right-0', 'z-[1]')} />
      </div>
      <div
        className={cn(
          'flex',
          'gap-1',
          'items-center',
          'justify-between',
          '-ml-2.5',
          headerClassName,
        )}
      >
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={back}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <h2 className="text-2xl font-bold">{title}</h2>
          {titleBadge}
        </div>

        <div className="flex items-center gap-2">
          {actionsSlot}
          <RefreshButton
            variant="outline"
            recordItemId={recordItemId}
            resource={resourceName}
          />
          {isEditButtonVisible && (
            <EditButton
              variant="outline"
              recordItemId={recordItemId}
              resource={resourceName}
            />
          )}
        </div>
      </div>
    </div>
  )
}

ShowView.displayName = 'ShowView'
